
import React, { useContext, useEffect, useState } from 'react';
import { SectionHeader } from '../../common';
import { CaretDownFilled, CaretUpFilled } from '@ant-design/icons';
import { Button, Dropdown, Menu, Spin } from 'antd';
import classNames from 'classnames';
import { getParams, setParams, setSearchParam } from '../../../branding/utils/urlManipulation';
import { Translate } from '../../../services/translationService/components/Translate';
import { PageContainer, PageContent, PaginationController } from '../../common';
import LearningList from '../../common/learningList';
import './styles.less';
import { useSearchLazyQuery } from '../../../gql/hooks/queries/useSearchLazyQuery';
import { useTranslations } from '../../../hooks/translations';
import { BreadcrumbContext } from '../../../breadcrumbs/BreadcrumbContext';

const keys = {
    courses: 'courses',
    curriculum: 'curricula',
    grid: 'card',
    list: 'list',
    zToA: 'za',
    aToZ: 'az'
};

export const MyLearningPage = ( { pageKey } ) => {

    const initialUrlParams = getParams();

    const { locale } = useTranslations();

    const { leaveCrumb } = useContext( BreadcrumbContext );    
    
    useEffect( () => {leaveCrumb( pageKey );}, [] );
    
    const [ pageData, setPageData ] = useState( initialUrlParams.page ? { page: parseInt( initialUrlParams.page ) } : { page: 1 } );
    // these store the keys, not the values
    const [ filterInfo, setFilterInfo ] = useState( initialUrlParams.filter ?? keys.courses ); 
    const [ alpha, setAlpha ] = useState( initialUrlParams.sort != keys.zToA ); 
    const [ view, setView ] = useState( initialUrlParams.view ?? keys.grid );

    const [ searchQuery, { data, loading: queryLoading, error, pageData: searchPageData } ] = useSearchLazyQuery( {}, false, false );

    const query = async ( page, sortAlpha, filter ) => {
        searchQuery( {
            types: [ filter ],
            query: '',
            page: page ?? 1,
            limit: 8, 
            sort: 'name',
            order: sortAlpha ? 'asc' : 'desc',
        } );
    };

    const [ paginationController, setPaginationController ] = useState();

    function fetch( { pageNumber, sortAlpha = alpha, filter = filterInfo } ) {

        const params = {
            page: pageNumber ?? pageData?.page,
            sort: sortAlpha ? keys.aToZ : keys.zToA,
            filter: filter
        };

        setParams( params );
        
        query( params.page, sortAlpha, filter );
    }

    useEffect( () => {
        fetch( { } );
    }, [ locale ] );

    function onPageChange( page ) {

        setPageData( { ...pageData, page } );
        fetch( { pageNumber: page } );
    }

    function onFilterChange( filter ) {

        paginationController?.setActivePage( 0 );
        setFilterInfo( filter );
        fetch( { filter, pageNumber: 1 } );
    }

    function selectFilter( opt ) {
        if ( filterInfo !== opt ) {
            onFilterChange( opt );
        }
    }

    function onSortChange( sortAlpha ) {

        paginationController?.setActivePage( 0 );
        setAlpha( sortAlpha );
        fetch( { sortAlpha, pageNumber: 1 } );
    }

    function onViewChange( viewInfo ) {
        setView( viewInfo );
        setSearchParam( 'view', viewInfo );
    }

    useEffect( () => {
        if ( searchPageData.totalResults !== undefined && !queryLoading ) {
            setPageData( searchPageData );
        }
    }, [ searchPageData, queryLoading ] );

    return (
        <PageContent className="page-home">
            <PageContainer>
                <SectionHeader>
                    <Translate.Text value="page.myLearning.headers.myLearning" />
                </SectionHeader>
            

            <div className='learning-content'>
                <div className='controls'>
                    <div className='filter-controls non-compact'>

                            <Button shape='round' key={ keys.courses } onClick={ () => selectFilter( keys.courses ) } className={ classNames( { active: filterInfo == keys.courses }, 'control' ) }>
                                <Translate.Text value='control.courses'/>
                            </Button> 

                            <Button shape='round' key={ keys.curriculum } onClick={ () => selectFilter( keys.curriculum ) } className={ classNames( { active: filterInfo == keys.curriculum }, 'control' ) }>
                                <Translate.Text value='control.curriculum'/>
                            </Button> 
                    </div>
                    <div className='filter-controls compact'>
                        <Dropdown 
                            trigger={ [ 'click' ] }
                            overlay={
                                <Menu>
                                    <Menu.Item key={ keys.courses } 
                                        className={ classNames( 'selectable', { selected: ( filterInfo == keys.courses ) } ) }
                                        onClick={ () => selectFilter( keys.courses ) } >
                                        <Translate.Text value='control.courses'/>
                                    </Menu.Item>
                                    <Menu.Item key={ keys.curriculum } 
                                        className={ classNames( 'selectable', { selected: ( filterInfo == keys.curriculum ) } ) }
                                        onClick={ () => selectFilter( keys.curriculum ) } >
                                        <Translate.Text value='control.curriculum'/>
                                    </Menu.Item>
                                </Menu>
                            }
                        >
                            <Button shape='round' className={ classNames( 'active', 'control' ) } >
                                <span className='button-text'>
                                    { filterInfo == keys.courses ? <Translate.Text value='control.courses'/> : <Translate.Text value='control.curriculum'/> }
                                    <CaretDownFilled />
                                </span>
                            </Button>
                                
                        </Dropdown>
                    </div>

                    <div className='sort-controls'>
                        <Button shape='round' onClick={ () => onSortChange( !alpha ) } className={ 'active control' } >
                            { alpha ? <><Translate.Text value="control.alphabetical" /><CaretDownFilled /></> : <><Translate.Text value="control.antiAlphabetical" /><CaretUpFilled /></> }
                        </Button> 
                    </div>

                    <div className='view-controls compact'>
                        <Dropdown 
                            trigger={ [ 'click' ] }
                            overlay={
                                <Menu>
                                    <Menu.Item key={ keys.list } 
                                        className={ classNames( 'selectable', { selected: ( view == keys.list ) } ) }
                                        onClick={ () => onViewChange( keys.list ) } >
                                        <Translate.Text value='courseList.viewOptions.list.label'/>
                                    </Menu.Item>
                                    <Menu.Item key={ keys.grid } 
                                        className={ classNames( 'selectable', { selected: ( view == keys.grid ) } ) }
                                        onClick={ () => onViewChange( keys.grid ) } >
                                        <Translate.Text value='courseList.viewOptions.grid.label'/>
                                    </Menu.Item>
                                </Menu>
                            }
                        >
                            <Button shape='round' className={ classNames( 'active', 'control' ) } >
                                <span className='button-text'>
                                    { view == keys.grid ? <Translate.Text value='courseList.viewOptions.grid.label'/> : <Translate.Text value='courseList.viewOptions.list.label'/> }
                                    <CaretDownFilled />
                                </span>
                            </Button>
                                
                        </Dropdown>
                    </div>
                    <div className='view-controls non-compact'>

                        <Button shape='round' key={ keys.list } onClick={ () => onViewChange( keys.list ) } className={ classNames( { active: view == keys.list }, 'control' ) }>
                            <Translate.Text value='courseList.viewOptions.list.label'/>
                        </Button> 

                        <Button shape='round' key={ keys.grid } onClick={ () => onViewChange( keys.grid ) } className={ classNames( { active: view == keys.grid }, 'control' ) }>
                            <Translate.Text value='courseList.viewOptions.grid.label'/>
                        </Button> 
                    </div>

                </div>
                <div className={ classNames( 'search-results', { empty: data?.length === 0 && error } ) }>
                    {
                        queryLoading || data == null ? <Spin /> : 
                            data?.length === 0 && error ? <div className='empty-page no-border'><Translate.Text className='title' value='page.searchLibrary.empty' /></div> : // nothing is returned from search
                            <LearningList
                                displayType={ view }
                                learningType={ filterInfo == keys.courses ? 'course' : 'curriculum' }
                                data={ data }
                                isLoading={ queryLoading }
                                />
                    }
                </div>
            </div>

            <div className={ classNames( 'learning-list-pagination-controller', { hidden: data?.length === 0 || error } ) }>
                { pageData?.totalResults > 0 &&
                    <PaginationController
                        pageSize={ pageData.limit }
                        initialPage={ pageData.page - 1 }
                        itemCount={ pageData.totalResults }
                        onChange={ ( index ) => onPageChange( index + 1 ) }
                        getController={ setPaginationController }
                    />
                }
                        
                </div>
                
            </PageContainer>
       </PageContent>
    );
};
